import "@/App.css";
import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { SplashScreen } from "./components/SplashScreen";

// Pages
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import Moto from "./pages/Moto";
import Modifiche from "./pages/Modifiche";
import Manutenzione from "./pages/Manutenzione";
import Viaggi from "./pages/Viaggi";
import Equipaggiamento from "./pages/Equipaggiamento";
import Spese from "./pages/Spese";
import Promemoria from "./pages/Promemoria";
import Documenti from "./pages/Documenti";
import Statistiche from "./pages/Statistiche";
import Wishlist from "./pages/Wishlist";
import Community from "./pages/Community";
import Impostazioni from "./pages/Impostazioni";
import StazioniBenzina from "./pages/StazioniBenzina";
import Abbonamento from "./pages/Abbonamento";
import AdminDashboard from "./pages/AdminDashboard";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import DownloadApp from "./pages/DownloadApp";

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Public Route (redirect to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Admin route - must be before other routes, has its own auth */}
      <Route path="/admin" element={<AdminDashboard />} />
      
      {/* Public routes */}
      <Route path="/login" element={
        <PublicRoute><Login /></PublicRoute>
      } />
      <Route path="/register" element={
        <PublicRoute><Register /></PublicRoute>
      } />
      
      {/* Protected routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute><Dashboard /></ProtectedRoute>
      } />
      <Route path="/moto" element={
        <ProtectedRoute><Moto /></ProtectedRoute>
      } />
      <Route path="/moto/:id" element={
        <ProtectedRoute><Moto /></ProtectedRoute>
      } />
      <Route path="/modifiche" element={
        <ProtectedRoute><Modifiche /></ProtectedRoute>
      } />
      <Route path="/manutenzione" element={
        <ProtectedRoute><Manutenzione /></ProtectedRoute>
      } />
      <Route path="/viaggi" element={
        <ProtectedRoute><Viaggi /></ProtectedRoute>
      } />
      <Route path="/equipaggiamento" element={
        <ProtectedRoute><Equipaggiamento /></ProtectedRoute>
      } />
      <Route path="/spese" element={
        <ProtectedRoute><Spese /></ProtectedRoute>
      } />
      <Route path="/promemoria" element={
        <ProtectedRoute><Promemoria /></ProtectedRoute>
      } />
      <Route path="/documenti" element={
        <ProtectedRoute><Documenti /></ProtectedRoute>
      } />
      <Route path="/statistiche" element={
        <ProtectedRoute><Statistiche /></ProtectedRoute>
      } />
      <Route path="/wishlist" element={
        <ProtectedRoute><Wishlist /></ProtectedRoute>
      } />
      <Route path="/community" element={
        <ProtectedRoute><Community /></ProtectedRoute>
      } />
      <Route path="/impostazioni" element={
        <ProtectedRoute><Impostazioni /></ProtectedRoute>
      } />
      <Route path="/stazioni-benzina" element={
        <ProtectedRoute><StazioniBenzina /></ProtectedRoute>
      } />
      <Route path="/abbonamento" element={
        <ProtectedRoute><Abbonamento /></ProtectedRoute>
      } />
      <Route path="/privacy" element={
        <ProtectedRoute><PrivacyPolicy /></ProtectedRoute>
      } />
      <Route path="/download" element={
        <ProtectedRoute><DownloadApp /></ProtectedRoute>
      } />
      
      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  const [showSplash, setShowSplash] = useState(true);
  const [splashComplete, setSplashComplete] = useState(false);

  // Check if splash was already shown in this session
  useEffect(() => {
    const splashShown = sessionStorage.getItem('splashShown');
    if (splashShown) {
      setShowSplash(false);
      setSplashComplete(true);
    }
  }, []);

  const handleSplashComplete = () => {
    sessionStorage.setItem('splashShown', 'true');
    setSplashComplete(true);
  };

  return (
    <BrowserRouter>
      <AuthProvider>
        {showSplash && <SplashScreen onComplete={handleSplashComplete} />}
        {splashComplete && (
          <>
            <AppRoutes />
            <Toaster 
              position="top-right"
              toastOptions={{
                style: {
                  background: '#0a0a0a',
                  border: '1px solid #262626',
                  color: '#fff',
                },
              }}
            />
          </>
        )}
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
